import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FieldError, Input, Label, TextField } from '@heroui/react';

import AuthLayout, { AuthLink } from '../components/common/AuthLayout';
import Notice from '../components/common/Notice';
import PasswordField from '../components/common/PasswordField';
import useValidation from '../components/common/useValidation';
import userService from '../services/auth.service';
import { fieldErrorsOf } from '../services/apiClient';
import * as check from '../services/validation';
import { messageOf } from '../i18n/el';

// Η οθόνη δημιουργίας λογαριασμού (ΛΑ-01).
//
// Οι τοπικοί κανόνες επικύρωσης είναι **οι ίδιοι** με τα σχήματα του API, σκόπιμα
// και όχι κατά σύμπτωση: ο χρήστης δεν πρέπει να μαθαίνει τον κανόνα **μετά** την
// υποβολή.

const EMPTY = { username: '', email: '', password: '', cpassword: '', fullName: '' };

export default function RegisterPage() {
    const [values, setValues] = useState(EMPTY);
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [successMsg, setSuccessMsg] = useState('');

    const form = useValidation();
    const navigate = useNavigate();

    const set = (name) => (value) => setValues((current) => ({ ...current, [name]: value }));

    // Οι τοπικοί κανόνες είναι **οι ίδιοι** με τα σχήματα zod του API, ώστε ο
    // χρήστης να μη μαθαίνει τον κανόνα μετά την υποβολή: κωδικός πέντε
    // χαρακτήρων περνούσε τον έλεγχο «όχι κενό» και απορριπτόταν με 422.
    const local = {
        username: check.username(values.username),
        email: check.email(values.email),
        password: check.password(values.password),
        cpassword: check.passwordConfirm(values.cpassword, values.password),
        fullName: check.fullName(values.fullName)
    };

    // Το μήνυμα του API έχει προτεραιότητα: ξέρει πράγματα που ο πελάτης δεν
    // μπορεί, π.χ. ότι το όνομα χρήστη είναι πιασμένο.
    const messageFor = (name) => fieldErrors[name] ?? form.show(name, local[name]);
    const isValid = Object.values(local).every((message) => message === undefined);

    function handleSubmit(event) {
        event.preventDefault();
        form.markSubmitted();
        setError('');
        setFieldErrors({});
        setSuccessMsg('');

        if (!isValid) return;

        setIsPending(true);
        const { username, email, password, fullName } = values;

        userService.register({ username, email, password, fullName }).then(
            () => {
                setSuccessMsg('Ο λογαριασμός σας δημιουργήθηκε. Μπορείτε να συνδεθείτε.');
                setValues(EMPTY);
                form.reset();
                setIsPending(false);
            },
            (apiError) => {
                // 422: το API επιστρέφει details ανά πεδίο. 409: διπλότυπο email ή
                // username, με details της μορφής { field } και χωρίς μήνυμα ανά πεδίο.
                const errors = fieldErrorsOf(apiError);
                if (apiError.status === 409 && apiError.details?.field) {
                    errors[apiError.details.field] = messageOf(apiError);
                }
                setError(messageOf(apiError));
                setFieldErrors(errors);
                setIsPending(false);
            }
        );
    }

    return (
        <AuthLayout
            title="Εγγραφή"
            subtitle="Δημιουργήστε λογαριασμό για να συμμετέχετε σε θεματικές συλλογές"
            width="max-w-2xl"
            footer={<AuthLink to="/login">Έχετε ήδη λογαριασμό; Συνδεθείτε</AuthLink>}
        >
            {error && <Notice status="danger" title="Η εγγραφή απέτυχε">{error}</Notice>}
            {successMsg && (
                <Notice status="success" title="Έτοιμο" onDismiss={() => setSuccessMsg('')}>
                    {successMsg}
                </Notice>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        name="username"
                        value={values.username}
                        onChange={set('username')}
                        onBlur={form.touch('username')}
                        isInvalid={Boolean(messageFor('username'))}
                        autoComplete="username"
                    >
                        <Label>Όνομα χρήστη</Label>
                        <Input />
                        <FieldError>{messageFor('username')}</FieldError>
                    </TextField>

                    <TextField
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={set('email')}
                        onBlur={form.touch('email')}
                        isInvalid={Boolean(messageFor('email'))}
                        autoComplete="email"
                    >
                        <Label>Διεύθυνση ηλεκτρονικού ταχυδρομείου</Label>
                        <Input placeholder="name@example.gr" />
                        <FieldError>{messageFor('email')}</FieldError>
                    </TextField>

                    <PasswordField
                        value={values.password}
                        onChange={set('password')}
                        onBlur={form.touch('password')}
                        autoComplete="new-password"
                        isInvalid={Boolean(messageFor('password'))}
                        errorMessage={messageFor('password')}
                    />

                    <PasswordField
                        label="Επιβεβαίωση κωδικού"
                        name="cpassword"
                        value={values.cpassword}
                        onChange={set('cpassword')}
                        onBlur={form.touch('cpassword')}
                        autoComplete="new-password"
                        isInvalid={Boolean(messageFor('cpassword'))}
                        errorMessage={messageFor('cpassword')}
                    />
                </div>

                <TextField
                    name="fullName"
                    value={values.fullName}
                    onChange={set('fullName')}
                    onBlur={form.touch('fullName')}
                    isInvalid={Boolean(messageFor('fullName'))}
                    autoComplete="name"
                >
                    <Label>Ονοματεπώνυμο</Label>
                    <Input />
                    <FieldError>{messageFor('fullName')}</FieldError>
                </TextField>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <Button type="submit" isPending={isPending} className="flex-1">
                        Εγγραφή
                    </Button>
                    <Button variant="outline" onPress={() => navigate('/login')} className="flex-1">
                        Ακύρωση
                    </Button>
                </div>
            </form>
        </AuthLayout>
    );
}
